import { ArrowLeft, Shield, Database, Lock, Share2, Clock, UserCheck, Cookie, Mail } from 'lucide-react';
import { Link } from 'react-router-dom';

const RGPD = () => {
  return (
    <div className="min-h-screen bg-slate-50 py-8 px-4" data-testid="rgpd-page">
      <div className="max-w-3xl mx-auto">
        <Link 
          to="/" 
          className="inline-flex items-center gap-2 text-slate-600 hover:text-[#8DC63F] mb-6 transition-colors"
        >
          <ArrowLeft className="h-4 w-4" />
          Retour à l'accueil
        </Link>

        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-8">
          <div className="flex items-center gap-3 mb-8">
            <div className="bg-[#8DC63F]/10 p-3 rounded-lg">
              <Shield className="h-8 w-8 text-[#8DC63F]" />
            </div>
            <div>
              <h1 className="text-3xl font-display font-bold text-[#0F172A]">
                Politique de confidentialité
              </h1>
              <p className="text-slate-500">& RGPD</p>
            </div>
          </div>

          {/* 1. Données collectées */}
          <section className="mb-8">
            <div className="flex items-center gap-3 mb-4">
              <div className="bg-blue-100 p-2 rounded-lg">
                <Database className="h-5 w-5 text-blue-600" />
              </div>
              <h2 className="text-xl font-semibold text-[#0F172A]">1. Données collectées</h2>
            </div>
            <div className="pl-12 text-slate-600">
              <p className="mb-2">L'application peut collecter les données suivantes :</p>
              <ul className="list-disc pl-5 space-y-1 text-sm">
                <li>Email</li>
                <li>Nom d'utilisateur</li>
                <li>Données techniques</li>
                <li>Données personnelles liées à la formation</li>
              </ul>
            </div>
          </section>

          {/* 2. Utilisation des données */}
          <section className="mb-8">
            <div className="flex items-center gap-3 mb-4">
              <div className="bg-green-100 p-2 rounded-lg">
                <UserCheck className="h-5 w-5 text-green-600" />
              </div>
              <h2 className="text-xl font-semibold text-[#0F172A]">2. Utilisation des données</h2>
            </div>
            <div className="pl-12 text-slate-600">
              <p className="mb-2">Les données sont utilisées uniquement pour :</p>
              <ul className="list-disc pl-5 space-y-1 text-sm">
                <li>Le fonctionnement de l'application</li>
                <li>Le suivi pédagogique</li>
                <li>La communication avec l'utilisateur</li>
              </ul>
            </div>
          </section>

          {/* 3. Stockage des données */}
          <section className="mb-8">
            <div className="flex items-center gap-3 mb-4">
              <div className="bg-purple-100 p-2 rounded-lg">
                <Lock className="h-5 w-5 text-purple-600" />
              </div>
              <h2 className="text-xl font-semibold text-[#0F172A]">3. Stockage des données</h2>
            </div>
            <div className="pl-12 text-slate-600">
              <p className="text-sm">
                Les données sont stockées de manière sécurisée et <strong>ne sont jamais revendues à des tiers</strong>.
              </p>
            </div>
          </section>

          {/* 4. Partage des données */}
          <section className="mb-8">
            <div className="flex items-center gap-3 mb-4">
              <div className="bg-orange-100 p-2 rounded-lg">
                <Share2 className="h-5 w-5 text-orange-600" />
              </div>
              <h2 className="text-xl font-semibold text-[#0F172A]">4. Partage des données</h2>
            </div>
            <div className="pl-12 text-slate-600">
              <p className="text-sm">
                <strong>Aucune donnée personnelle n'est partagée avec des tiers</strong>, sauf obligation légale.
              </p>
            </div>
          </section>

          {/* 5. Durée de conservation */}
          <section className="mb-8">
            <div className="flex items-center gap-3 mb-4">
              <div className="bg-teal-100 p-2 rounded-lg">
                <Clock className="h-5 w-5 text-teal-600" />
              </div>
              <h2 className="text-xl font-semibold text-[#0F172A]">5. Durée de conservation</h2>
            </div>
            <div className="pl-12 text-slate-600">
              <p className="text-sm">
                Les données sont conservées uniquement pendant la durée nécessaire à l'utilisation de l'application.
              </p>
            </div>
          </section>

          {/* 6. Droits des utilisateurs */}
          <section className="mb-8">
            <div className="flex items-center gap-3 mb-4">
              <div className="bg-[#8DC63F]/10 p-2 rounded-lg">
                <Shield className="h-5 w-5 text-[#8DC63F]" />
              </div>
              <h2 className="text-xl font-semibold text-[#0F172A]">6. Droits des utilisateurs</h2>
            </div>
            <div className="pl-12 text-slate-600">
              <p className="mb-3 text-sm">Conformément au RGPD, vous disposez des droits suivants :</p>
              <ul className="list-disc pl-5 space-y-1 text-sm mb-4">
                <li><strong>Droit d'accès</strong> - Obtenir une copie de vos données</li>
                <li><strong>Droit de rectification</strong> - Corriger vos données</li>
                <li><strong>Droit de suppression</strong> - Demander l'effacement de vos données</li>
              </ul>
              <div className="bg-slate-50 p-4 rounded-lg border border-slate-200">
                <p className="text-sm mb-2">Vous pouvez exercer ces droits en contactant :</p>
                <a 
                  href="mailto:contact@fisl.fr" 
                  className="inline-flex items-center gap-2 text-[#8DC63F] hover:underline font-medium"
                >
                  <Mail className="h-4 w-4" />
                  contact@fisl.fr
                </a>
              </div>
            </div>
          </section>

          {/* 7. Cookies */}
          <section className="mb-8">
            <div className="flex items-center gap-3 mb-4">
              <div className="bg-amber-100 p-2 rounded-lg">
                <Cookie className="h-5 w-5 text-amber-600" />
              </div>
              <h2 className="text-xl font-semibold text-[#0F172A]">7. Cookies</h2>
            </div>
            <div className="pl-12 text-slate-600">
              <p className="text-sm">
                L'application utilise uniquement des <strong>cookies techniques</strong> nécessaires au fonctionnement.
              </p>
            </div>
          </section>

          {/* Lien Mentions légales */}
          <div className="text-center pt-4 border-t border-slate-200">
            <Link 
              to="/mentions-legales" 
              className="text-[#8DC63F] hover:underline font-medium"
            >
              ← Voir les Mentions légales
            </Link>
          </div>
        </div>

        <p className="text-center text-xs text-slate-400 mt-6">
          © {new Date().getFullYear()} FISL - Formation Internationale aux Sports de Loisirs
        </p>
      </div>
    </div>
  );
};

export default RGPD;
